import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { loadLanguage } from "../i18n";
import { useFeatureFlags } from "./FeatureFlagContext";
import API from "../constants/API";
import { authHeader } from "../common/mainfunction";
import { getUserId, getTeamID } from "../common/function";
import { GET_AVAILABLE_LANGUAGES_URL, UPDATE_LANGUAGE_PREFERENCE_URL } from "../common/url";
import type { FeatureFlags } from "../types/api";

interface LanguageOption {
  languageCode: string;
  languageName: string;
  nativeName: string;
}

interface LanguageContextValue {
  language: string;
  setLanguage: (lang: string) => Promise<void>;
  availableLanguages: LanguageOption[];
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: "en",
  setLanguage: async () => {},
  availableLanguages: [{ languageCode: "en", languageName: "English", nativeName: "English" }],
  isLoading: false,
});

const STORAGE_KEY = "ezcloud_language";

interface LanguageProviderProps {
  children: React.ReactNode;
}

/**
 * Resolution hierarchy:
 *  1. User preference (tbluser.languagePreference)
 *  2. Tenant default  (feature_flags.defaultLanguage)
 *  3. localStorage    (previous session choice)
 *  4. Fallback        ("en")
 */
export function LanguageProvider({ children }: LanguageProviderProps) {
  const { i18n } = useTranslation();
  const featureFlags = useFeatureFlags() as FeatureFlags;
  const i18nEnabled = featureFlags.i18nEnabled === true;
  const tenantDefault = featureFlags.defaultLanguage || "en";

  const [language, setLanguageState] = useState<string>("en");
  const [availableLanguages, setAvailableLanguages] = useState<LanguageOption[]>([
    { languageCode: "en", languageName: "English", nativeName: "English" },
  ]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch available languages from API
  useEffect(() => {
    if (!i18nEnabled) return;

    const fetchLanguages = async () => {
      try {
        const teamId = getTeamID();
        if (!teamId) return;

        const response = await API({
          method: "GET",
          url: GET_AVAILABLE_LANGUAGES_URL,
          headers: authHeader(),
          params: { teamId },
        });

        const parsed =
          typeof response.data === "string" ? JSON.parse(response.data) : response.data;
        if (parsed.status === "Success" && parsed.data?.languages) {
          setAvailableLanguages(parsed.data.languages);
        }
      } catch {
        // Silently fall back to English-only
      }
    };

    fetchLanguages();
  }, [i18nEnabled]);

  // Resolve effective language on mount
  useEffect(() => {
    if (!i18nEnabled) return;

    const resolve = async () => {
      // Try user preference from profile API (already loaded by EditProfile)
      // For now, check localStorage first, then tenant default
      const stored = localStorage.getItem(STORAGE_KEY);
      const effective = stored || tenantDefault || "en";

      if (effective !== "en") {
        await loadLanguage(effective);
      }
      i18n.changeLanguage(effective);
      setLanguageState(effective);
    };

    resolve();
  }, [i18nEnabled, tenantDefault, i18n]);

  // Change language — persists to API + localStorage
  const setLanguage = useCallback(
    async (lang: string) => {
      setIsLoading(true);
      try {
        // Load translation bundles
        await loadLanguage(lang);
        i18n.changeLanguage(lang);
        setLanguageState(lang);
        localStorage.setItem(STORAGE_KEY, lang);

        // Persist to backend
        const userId = getUserId();
        if (userId) {
          await API({
            method: "PUT",
            url: UPDATE_LANGUAGE_PREFERENCE_URL,
            headers: authHeader(),
            data: {
              userId,
              languagePreference: lang === tenantDefault ? null : lang,
            },
          });
        }
      } catch (err) {
        console.error("[LanguageContext] Failed to change language:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [i18n, tenantDefault]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, availableLanguages, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = (): LanguageContextValue => useContext(LanguageContext);

export default LanguageContext;
